import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ColDef } from 'ag-grid-community';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import GelatoDataGrid from '../components/DataGrid/GelatoDataGrid';
import { buildWorksheetFromRows, exportWorkbookToXlsx } from '../utils/excelExport';
import {
  FiltriAnalytics,
  AnalyticsGusto,
  AnalyticsIngrediente,
  AnalyticsPeriodo,
  AnalyticsConfronto,
  AnalyticsFoodCostGusti,
  Ingrediente,
  PuntoVendita,
  CategoriaGusto
} from '../types';
import ApiService from '../services/api';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Download,
  GitCompare,
  Target,
  Activity,
  CalendarDays,
  Layers,
  Euro,
  RotateCcw
} from 'lucide-react';
import {
  PageContainerWide,
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Button,
  FormGroup,
  Label,
  Select,
  LoadingState,
  Alert,
  Badge,
  StatCard,
  StatGrid
} from '../components/ui';

// Colori delle serie del grafico (unica eccezione hex consentita: recharts
// non legge i custom property CSS negli attributi SVG).
const COLORE_SERIE_ANNO = '#7d8202';
const COLORE_SERIE_CONFRONTO = '#C17637';

const MESI_BREVI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

// --- Helper numerici ---------------------------------------------------------

const ensureNumber = (value: unknown, defaultValue = 0): number => {
  if (value === null || value === undefined) return defaultValue;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isNaN(parsed) ? defaultValue : parsed;
};

const nfDecimale = new Intl.NumberFormat('it-IT', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});
const nfIntero = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 });
const nfEuro = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

const formatNumber = (value: unknown) => nfDecimale.format(ensureNumber(value));
const formatInt = (value: unknown) => nfIntero.format(ensureNumber(value));
const formatCurrency = (value: unknown) => nfEuro.format(ensureNumber(value));

const normalizzaConfronto = (data: AnalyticsConfronto | null | undefined, anno: number, confrontaAnno: number): AnalyticsConfronto => ({
  anno_corrente: {
    anno: ensureNumber(data?.anno_corrente?.anno, anno),
    kg_totale: ensureNumber(data?.anno_corrente?.kg_totale),
    costo_totale: ensureNumber(data?.anno_corrente?.costo_totale),
    margine: ensureNumber(data?.anno_corrente?.margine),
    dati_mensili: Array.isArray(data?.anno_corrente?.dati_mensili) ? data!.anno_corrente.dati_mensili : []
  },
  anno_precedente: {
    anno: ensureNumber(data?.anno_precedente?.anno, confrontaAnno),
    kg_totale: ensureNumber(data?.anno_precedente?.kg_totale),
    costo_totale: ensureNumber(data?.anno_precedente?.costo_totale),
    margine: ensureNumber(data?.anno_precedente?.margine),
    dati_mensili: Array.isArray(data?.anno_precedente?.dati_mensili) ? data!.anno_precedente!.dati_mensili : []
  },
  variazioni: {
    kg_percentuale: ensureNumber(data?.variazioni?.kg_percentuale),
    costo_percentuale: ensureNumber(data?.variazioni?.costo_percentuale),
    margine_percentuale: ensureNumber(data?.variazioni?.margine_percentuale)
  }
});

// --- Styled ------------------------------------------------------------------

const SectionCard = styled(Card)`
  margin-bottom: var(--space-xl);
`;

const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--space-md);
  align-items: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ResetWrap = styled.div`
  display: flex;
  align-items: flex-end;

  @media (max-width: 768px) {
    grid-column: 1 / -1;
  }
`;

const Num = styled.span`
  font-variant-numeric: tabular-nums;
`;

const ChartWrap = styled.div`
  width: 100%;
  height: 320px;

  @media (max-width: 768px) {
    height: 260px;
  }
`;

const VariationsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-md);
  margin-top: var(--space-lg);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const VariationItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-md);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-md);
`;

const VariationLabel = styled.span`
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
`;

const VariationValue = styled.span`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
`;

const SectionNote = styled.span`
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  font-weight: var(--font-weight-normal);
  text-align: right;
`;

const LocalFilterRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: var(--space-md);
  padding: var(--space-md) var(--space-lg);
  border-bottom: 1px solid var(--gray-100);

  > div {
    min-width: 240px;
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    > div {
      min-width: 0;
      flex: 1;
    }
  }
`;

const ChartFootnote = styled(Alert)`
  margin-top: var(--space-lg);
  margin-bottom: 0;
`;

const EmptyNote = styled.p`
  margin: 0;
  padding: var(--space-lg);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
`;

const numericCellStyle = { fontVariantNumeric: 'tabular-nums' } as const;

// --- Componente --------------------------------------------------------------

const AnalyticsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dati per i filtri
  const [ingredienti, setIngredienti] = useState<Ingrediente[]>([]);
  const [puntiVendita, setPuntiVendita] = useState<PuntoVendita[]>([]);
  const [categorieGusti, setCategorieGusti] = useState<CategoriaGusto[]>([]);

  // Dati analytics
  const [analyticsGusti, setAnalyticsGusti] = useState<AnalyticsGusto[]>([]);
  const [analyticsIngredienti, setAnalyticsIngredienti] = useState<AnalyticsIngrediente[]>([]);
  const [analyticsPeriodo, setAnalyticsPeriodo] = useState<AnalyticsPeriodo[]>([]);
  const [analyticsConfronto, setAnalyticsConfronto] = useState<AnalyticsConfronto | null>(null);
  const [foodCostGusti, setFoodCostGusti] = useState<AnalyticsFoodCostGusti | null>(null);

  // Filtri globali (applicati automaticamente al cambio)
  const [filtri, setFiltri] = useState<FiltriAnalytics>({
    anno: new Date().getFullYear(),
    confrontaAnno: new Date().getFullYear() - 1
  });

  // Filtro Ingrediente locale alla sezione Ingredienti
  // (/ingredienti-avanzate è l'unico endpoint che lo usa)
  const [ingredienteFiltro, setIngredienteFiltro] = useState<number | undefined>(undefined);

  // Dati di supporto ai filtri (una volta sola)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ingredientiData, puntiVenditaData, categorieData] = await Promise.all([
          ApiService.getIngredienti(),
          ApiService.getPuntiVendita(),
          ApiService.getCategorieGusti()
        ]);
        if (cancelled) return;
        setIngredienti(Array.isArray(ingredientiData) ? ingredientiData : []);
        setPuntiVendita(Array.isArray(puntiVenditaData) ? puntiVenditaData : []);
        setCategorieGusti(Array.isArray(categorieData) ? categorieData : []);
      } catch (err) {
        console.error('Errore caricamento dati filtri:', err);
        if (!cancelled) setError('Errore nel caricamento dei dati di supporto ai filtri');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Dati principali: si ricaricano automaticamente al cambio dei filtri
  useEffect(() => {
    let cancelled = false;
    const anno = ensureNumber(filtri.anno, new Date().getFullYear());
    const confrontaAnno = ensureNumber(filtri.confrontaAnno, anno - 1);

    (async () => {
      setLoading(true);
      setError(null);

      const [gustiRes, periodoRes, confrontoRes, foodCostRes] = await Promise.allSettled([
        ApiService.getAnalyticsGusti(filtri),
        ApiService.getAnalyticsPeriodo(filtri),
        ApiService.getAnalyticsConfronto(filtri),
        ApiService.getGustiRedditizi(filtri, 100)
      ]);
      if (cancelled) return;

      let hasErrors = false;

      if (gustiRes.status === 'fulfilled' && Array.isArray(gustiRes.value)) {
        setAnalyticsGusti(gustiRes.value);
      } else {
        hasErrors = true;
        setAnalyticsGusti([]);
      }

      if (periodoRes.status === 'fulfilled' && Array.isArray(periodoRes.value)) {
        setAnalyticsPeriodo(periodoRes.value);
      } else {
        hasErrors = true;
        setAnalyticsPeriodo([]);
      }

      if (confrontoRes.status === 'fulfilled' && confrontoRes.value) {
        setAnalyticsConfronto(normalizzaConfronto(confrontoRes.value, anno, confrontaAnno));
      } else {
        hasErrors = true;
        setAnalyticsConfronto(normalizzaConfronto(null, anno, confrontaAnno));
      }

      if (foodCostRes.status === 'fulfilled' && foodCostRes.value && Array.isArray(foodCostRes.value.gusti)) {
        setFoodCostGusti(foodCostRes.value);
      } else {
        hasErrors = true;
        setFoodCostGusti(null);
      }

      if (hasErrors) {
        setError('Si sono verificati alcuni errori durante il caricamento dei dati. Alcune informazioni potrebbero essere incomplete.');
      }
      setLoading(false);
      setFirstLoadDone(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [filtri]);

  // Ingredienti: filtri globali + filtro ingrediente locale alla sezione
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await ApiService.getAnalyticsIngredienti({ ...filtri, ingredienteId: ingredienteFiltro });
        if (!cancelled) setAnalyticsIngredienti(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn('Errore caricamento analytics ingredienti:', err);
        if (!cancelled) setAnalyticsIngredienti([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filtri, ingredienteFiltro]);

  const handleFilterChange = (key: keyof FiltriAnalytics, rawValue: string) => {
    setFiltri(prev => {
      if (key === 'categoriaGusto') {
        return { ...prev, categoriaGusto: rawValue || undefined };
      }
      const numValue = rawValue !== '' ? ensureNumber(rawValue) : undefined;
      return { ...prev, [key]: numValue };
    });
  };

  const resetFilters = () => {
    setFiltri({
      anno: new Date().getFullYear(),
      confrontaAnno: new Date().getFullYear() - 1
    });
    setIngredienteFiltro(undefined);
  };

  // --- KPI del periodo filtrato (da /analytics/periodo-dettagli + /analytics/gusti)
  const kpi = useMemo(() => {
    const kg = analyticsPeriodo.reduce((sum, r) => sum + ensureNumber(r.kg_totale), 0);
    const costo = analyticsPeriodo.reduce((sum, r) => sum + ensureNumber(r.costo_totale), 0);
    return {
      kg,
      costo,
      foodCostMedio: kg > 0 ? costo / kg : 0,
      giorniProduzione: analyticsPeriodo.length,
      gustiProdotti: analyticsGusti.length
    };
  }, [analyticsPeriodo, analyticsGusti]);

  // --- Dati del grafico confronto mensile
  const confrontoAttivo = filtri.confrontaAnno !== undefined;
  const chartData = useMemo(() => {
    if (!analyticsConfronto) return [];
    const correnti = new Map(analyticsConfronto.anno_corrente.dati_mensili.map(m => [m.mese, ensureNumber(m.kg_totale)]));
    const precedenti = new Map((analyticsConfronto.anno_precedente?.dati_mensili || []).map(m => [m.mese, ensureNumber(m.kg_totale)]));
    return MESI_BREVI.map((nome, i) => ({
      mese: nome,
      annoCorrente: correnti.get(i + 1) ?? 0,
      annoConfronto: precedenti.get(i + 1) ?? 0
    }));
  }, [analyticsConfronto]);

  const exportData = () => {
    try {
      const gustiWs = buildWorksheetFromRows(analyticsGusti, [
        { header: 'Gusto', value: (r: AnalyticsGusto) => r.nome },
        { header: 'Categoria', value: (r: AnalyticsGusto) => r.categoria },
        { header: 'Kg Totali', value: (r: AnalyticsGusto) => ensureNumber(r.totale_kg) },
        { header: 'Produzioni', value: (r: AnalyticsGusto) => ensureNumber(r.num_produzioni) },
        { header: 'Media per Produzione (kg)', value: (r: AnalyticsGusto) => ensureNumber(r.media_per_produzione) },
        { header: '% sul Totale', value: (r: AnalyticsGusto) => `${ensureNumber(r.percentuale).toFixed(1)}%` }
      ]);

      const foodCostRows = foodCostGusti?.gusti || [];
      const foodCostWs = buildWorksheetFromRows(foodCostRows, [
        { header: 'Gusto', value: r => r.nome },
        { header: 'Categoria', value: r => r.categoria },
        { header: 'Food Cost €/kg', value: r => ensureNumber(r.costo_per_kg) },
        { header: 'Kg Prodotti', value: r => ensureNumber(r.totale_kg_prodotti) },
        { header: 'Incidenza %', value: r => `${ensureNumber(r.incidenza_percentuale).toFixed(1)}%` },
        { header: 'Margine Teorico €/kg', value: r => ensureNumber(r.margine_kg) },
        { header: 'Prezzo Riferimento €/kg', value: () => ensureNumber(foodCostGusti?.prezzo_riferimento_kg) }
      ]);

      const costoTotaleIngredienti = analyticsIngredienti.reduce((sum, r) => sum + ensureNumber(r.costo_totale), 0);
      const ingredientiRows: Array<Record<string, unknown>> = [
        ...analyticsIngredienti.map(r => ({
          nome: r.nome,
          consumo: `${formatNumber(r.consumo_totale)} ${r.unita}`,
          costo_totale: ensureNumber(r.costo_totale),
          gusti_utilizzo: (r.utilizzo_gusti || []).map(g => g.gusto_nome).join(', ')
        })),
        { nome: 'TOTALE', consumo: '', costo_totale: costoTotaleIngredienti, gusti_utilizzo: '' }
      ];
      const ingredientiWs = buildWorksheetFromRows(ingredientiRows, [
        { header: 'Ingrediente', value: r => r.nome },
        { header: 'Consumo Totale', value: r => r.consumo },
        { header: 'Costo Totale', value: r => formatCurrency(r.costo_totale) },
        { header: 'Gusti che lo utilizzano', value: r => r.gusti_utilizzo }
      ]);

      const annoLabel = filtri.anno ? String(filtri.anno) : 'tutti';
      exportWorkbookToXlsx(`analytics-${annoLabel}-${new Date().toISOString().split('T')[0]}.xlsx`, [
        { name: 'Gusti', worksheet: gustiWs },
        { name: 'Food Cost Gusti', worksheet: foodCostWs },
        { name: 'Ingredienti', worksheet: ingredientiWs }
      ]);
    } catch (err) {
      console.error('Errore export:', err);
      setError('Errore nell\'esportazione dei dati');
    }
  };

  // --- Colonne griglie -------------------------------------------------------

  const renderGustoCell = (params: any) => {
    const colore = params?.data?.colore;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span
          style={{
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            backgroundColor: colore || 'var(--gray-300)',
            flexShrink: 0,
            display: 'inline-block'
          }}
        />
        {params.value}
      </div>
    );
  };

  const gustiColumnDefs: ColDef[] = [
    { headerName: 'Gusto', field: 'nome', minWidth: 220, cellRenderer: renderGustoCell },
    { headerName: 'Categoria', field: 'categoria', minWidth: 150 },
    {
      headerName: 'Kg Totali',
      field: 'totale_kg',
      minWidth: 120,
      cellStyle: numericCellStyle,
      valueFormatter: (p: any) => `${formatNumber(p.value)} kg`
    },
    {
      headerName: 'Produzioni',
      field: 'num_produzioni',
      minWidth: 120,
      cellStyle: numericCellStyle,
      valueFormatter: (p: any) => formatInt(p.value)
    },
    {
      headerName: 'Media per Produzione',
      field: 'media_per_produzione',
      minWidth: 170,
      cellStyle: numericCellStyle,
      valueFormatter: (p: any) => `${formatNumber(p.value)} kg`
    },
    {
      headerName: '% sul Totale',
      field: 'percentuale',
      minWidth: 130,
      cellStyle: numericCellStyle,
      valueFormatter: (p: any) => `${formatNumber(p.value)}%`
    }
  ];

  const foodCostColumnDefs: ColDef[] = [
    { headerName: 'Gusto', field: 'nome', minWidth: 220, cellRenderer: renderGustoCell },
    { headerName: 'Categoria', field: 'categoria', minWidth: 140 },
    {
      headerName: 'Food Cost €/kg',
      field: 'costo_per_kg',
      minWidth: 140,
      cellStyle: numericCellStyle,
      valueFormatter: (p: any) => formatCurrency(p.value)
    },
    {
      headerName: 'Kg Prodotti',
      field: 'totale_kg_prodotti',
      minWidth: 130,
      cellStyle: numericCellStyle,
      valueFormatter: (p: any) => `${formatNumber(p.value)} kg`
    },
    {
      headerName: 'Incidenza %',
      field: 'incidenza_percentuale',
      minWidth: 130,
      valueFormatter: (p: any) => `${formatNumber(p.value)}%`,
      cellStyle: (params: any) => ({
        ...numericCellStyle,
        fontWeight: 'bold',
        color: ensureNumber(params.value) <= 30
          ? 'var(--success)'
          : ensureNumber(params.value) <= 40
            ? 'var(--warning)'
            : 'var(--error)'
      })
    },
    {
      headerName: 'Margine Teorico €/kg',
      field: 'margine_kg',
      minWidth: 170,
      valueFormatter: (p: any) => formatCurrency(p.value),
      cellStyle: (params: any) => ({
        ...numericCellStyle,
        fontWeight: 'bold',
        color: ensureNumber(params.value) > 0 ? 'var(--success)' : 'var(--error)'
      })
    }
  ];

  const ingredientiColumnDefs: ColDef[] = [
    {
      headerName: 'Ingrediente',
      field: 'nome',
      minWidth: 220,
      cellStyle: (params: any) => (params.node.rowPinned ? { fontWeight: 'bold' } : undefined)
    },
    {
      headerName: 'Consumo Totale',
      field: 'consumo_totale',
      minWidth: 160,
      cellStyle: numericCellStyle,
      valueFormatter: (p: any) => {
        if (p.node.rowPinned) return '';
        const unita = p?.data?.unita || '';
        return `${formatNumber(p.value)} ${unita}`;
      }
    },
    {
      headerName: 'Costo Totale',
      field: 'costo_totale',
      minWidth: 150,
      valueFormatter: (p: any) => formatCurrency(p.value),
      cellStyle: (params: any) =>
        params.node.rowPinned
          ? { ...numericCellStyle, fontWeight: 'bold', color: 'var(--error)' }
          : numericCellStyle
    },
    {
      headerName: 'Gusti che lo utilizzano',
      minWidth: 280,
      valueGetter: (p: any) => {
        if (p.node.rowPinned) return '';
        return (p?.data?.utilizzo_gusti || []).map((g: any) => g.gusto_nome).join(', ');
      }
    }
  ];

  // --- Render ----------------------------------------------------------------

  if (loading && !firstLoadDone) {
    return (
      <PageContainerWide>
        <LoadingState />
      </PageContainerWide>
    );
  }

  const dataOggi = new Date().toISOString().split('T')[0];
  const annoLabel = filtri.anno ? String(filtri.anno) : 'tutti';
  const variazioni = analyticsConfronto?.variazioni;

  const renderVariazione = (percentuale: number, positivoBuono: boolean) => {
    const positiva = percentuale >= 0;
    const buono = positivoBuono ? positiva : !positiva;
    return (
      <Badge $variant={buono ? 'success' : 'error'}>
        {positiva ? <TrendingUp /> : <TrendingDown />}
        <Num>{formatNumber(Math.abs(percentuale))}%</Num>
      </Badge>
    );
  };

  return (
    <PageContainerWide>
      <PageHeader
        title="Analytics"
        icon={<BarChart3 />}
        actions={
          <Button $variant="secondary" onClick={exportData}>
            <Download size={16} />
            Esporta
          </Button>
        }
      />

      {error && <Alert $variant="error">{error}</Alert>}

      {/* Filtri: applicati automaticamente al cambio */}
      <SectionCard>
        <CardBody>
          <FiltersRow>
            <FormGroup>
              <Label>Anno</Label>
              <Select
                value={filtri.anno || ''}
                onChange={e => handleFilterChange('anno', e.target.value)}
              >
                {Array.from({ length: 10 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Mese</Label>
              <Select
                value={filtri.mese || ''}
                onChange={e => handleFilterChange('mese', e.target.value)}
              >
                <option value="">Tutti i mesi</option>
                {MESI_BREVI.map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2023, i, 1).toLocaleDateString('it-IT', { month: 'long' })}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Punto Vendita</Label>
              <Select
                value={filtri.puntoVenditaId || ''}
                onChange={e => handleFilterChange('puntoVenditaId', e.target.value)}
              >
                <option value="">Tutti i punti vendita</option>
                {puntiVendita.map(pv => (
                  <option key={pv.id} value={pv.id}>
                    {pv.nome}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Categoria Gusto</Label>
              <Select
                value={filtri.categoriaGusto || ''}
                onChange={e => handleFilterChange('categoriaGusto', e.target.value)}
              >
                <option value="">Tutte le categorie</option>
                {categorieGusti.filter(cat => cat.attivo).map(cat => (
                  <option key={cat.id} value={cat.nome}>
                    {cat.nome}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Confronta con</Label>
              <Select
                value={filtri.confrontaAnno || ''}
                onChange={e => handleFilterChange('confrontaAnno', e.target.value)}
              >
                <option value="">Nessun confronto</option>
                {Array.from({ length: 10 }, (_, i) => {
                  const year = new Date().getFullYear() - i - 1;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </Select>
            </FormGroup>

            <ResetWrap>
              <Button $variant="secondary" onClick={resetFilters}>
                <RotateCcw size={16} />
                Reset
              </Button>
            </ResetWrap>
          </FiltersRow>
        </CardBody>
      </SectionCard>

      {/* KPI del periodo filtrato */}
      <StatGrid>
        <StatCard
          icon={<BarChart3 />}
          value={<Num>{formatNumber(kpi.kg)} kg</Num>}
          label="Produzione nel periodo"
        />
        <StatCard
          icon={<Euro />}
          value={<Num>{formatCurrency(kpi.costo)}</Num>}
          label="Costo ingredienti"
        />
        <StatCard
          icon={<DollarSign />}
          value={<Num>{formatCurrency(kpi.foodCostMedio)}/kg</Num>}
          label="Food cost medio"
        />
        <StatCard
          icon={<CalendarDays />}
          value={<Num>{formatInt(kpi.giorniProduzione)}</Num>}
          label="Giorni di produzione"
        />
        <StatCard
          icon={<Layers />}
          value={<Num>{formatInt(kpi.gustiProdotti)}</Num>}
          label="Gusti prodotti"
        />
      </StatGrid>

      {/* Confronto mensile */}
      {analyticsConfronto && (
        <SectionCard>
          <CardHeader>
            <h3>
              <GitCompare size={20} style={{ verticalAlign: 'text-bottom', marginRight: '0.5rem' }} />
              Produzione mensile {analyticsConfronto.anno_corrente.anno}
              {confrontoAttivo ? ` vs ${analyticsConfronto.anno_precedente?.anno}` : ''}
            </h3>
          </CardHeader>
          <CardBody>
            <ChartWrap>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="mese" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v: number) => formatInt(v)} width={56} />
                  <RechartsTooltip formatter={(value: any, name: any) => [`${formatNumber(value)} kg`, name]} />
                  <Legend />
                  <Bar
                    dataKey="annoCorrente"
                    name={String(analyticsConfronto.anno_corrente.anno)}
                    fill={COLORE_SERIE_ANNO}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={28}
                  />
                  {confrontoAttivo && (
                    <Bar
                      dataKey="annoConfronto"
                      name={String(analyticsConfronto.anno_precedente?.anno)}
                      fill={COLORE_SERIE_CONFRONTO}
                      fillOpacity={0.7}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={28}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </ChartWrap>

            {confrontoAttivo && variazioni && (
              <VariationsRow>
                <VariationItem>
                  <VariationLabel>Produzione {analyticsConfronto.anno_corrente.anno}</VariationLabel>
                  <VariationValue>{formatNumber(analyticsConfronto.anno_corrente.kg_totale)} kg</VariationValue>
                  <div>{renderVariazione(variazioni.kg_percentuale, true)}</div>
                </VariationItem>
                <VariationItem>
                  <VariationLabel>Costo ingredienti {analyticsConfronto.anno_corrente.anno}</VariationLabel>
                  <VariationValue>{formatCurrency(analyticsConfronto.anno_corrente.costo_totale)}</VariationValue>
                  <div>{renderVariazione(variazioni.costo_percentuale, false)}</div>
                </VariationItem>
                <VariationItem>
                  <VariationLabel>Margine teorico {analyticsConfronto.anno_corrente.anno}</VariationLabel>
                  <VariationValue>{formatCurrency(analyticsConfronto.anno_corrente.margine)}</VariationValue>
                  <div>{renderVariazione(variazioni.margine_percentuale, true)}</div>
                </VariationItem>
              </VariationsRow>
            )}

            <ChartFootnote $variant="info">
              Ricavi e margine sono teorici: kg × prezzo €/kg del punto vendita (default 30 €/kg).
            </ChartFootnote>
          </CardBody>
        </SectionCard>
      )}

      {/* Gusti per produzione */}
      <SectionCard>
        <CardHeader>
          <h3>
            <Target size={20} style={{ verticalAlign: 'text-bottom', marginRight: '0.5rem' }} />
            Gusti per Produzione
          </h3>
          {filtri.mese && <SectionNote>il filtro mese non si applica a questa sezione</SectionNote>}
        </CardHeader>
        {analyticsGusti.length > 0 ? (
          <GelatoDataGrid
            rowData={analyticsGusti}
            columnDefs={gustiColumnDefs}
            pagination={true}
            height="600px"
            exportable={true}
            excelFileName={`gusti-${annoLabel}-${dataOggi}.xlsx`}
            csvFileName={`gusti-${annoLabel}-${dataOggi}.csv`}
          />
        ) : (
          <EmptyNote>Nessun dato per i filtri selezionati.</EmptyNote>
        )}
      </SectionCard>

      {/* Food cost per gusto */}
      <SectionCard>
        <CardHeader>
          <h3>
            <DollarSign size={20} style={{ verticalAlign: 'text-bottom', marginRight: '0.5rem' }} />
            Food Cost per Gusto
          </h3>
        </CardHeader>
        {foodCostGusti && foodCostGusti.gusti.length > 0 ? (
          <>
            <CardBody style={{ paddingBottom: 0 }}>
              <Alert $variant="info">
                Margini calcolati sul prezzo di riferimento di{' '}
                <strong>
                  <Num>{formatNumber(foodCostGusti.prezzo_riferimento_kg)} €/kg</Num>
                </strong>{' '}
                {filtri.puntoVenditaId
                  ? '(prezzo del punto vendita selezionato)'
                  : '(media dei punti vendita)'}
                .
              </Alert>
            </CardBody>
            <GelatoDataGrid
              rowData={foodCostGusti.gusti}
              columnDefs={foodCostColumnDefs}
              pagination={true}
              height="600px"
              exportable={true}
              excelFileName={`food-cost-gusti-${annoLabel}-${dataOggi}.xlsx`}
              csvFileName={`food-cost-gusti-${annoLabel}-${dataOggi}.csv`}
            />
          </>
        ) : (
          <EmptyNote>Nessun dato per i filtri selezionati.</EmptyNote>
        )}
      </SectionCard>

      {/* Ingredienti */}
      <SectionCard>
        <CardHeader>
          <h3>
            <Activity size={20} style={{ verticalAlign: 'text-bottom', marginRight: '0.5rem' }} />
            Analytics Ingredienti
          </h3>
        </CardHeader>
        <LocalFilterRow>
          <FormGroup>
            <Label>Ingrediente</Label>
            <Select
              value={ingredienteFiltro || ''}
              onChange={e => setIngredienteFiltro(e.target.value !== '' ? ensureNumber(e.target.value) : undefined)}
            >
              <option value="">Tutti gli ingredienti</option>
              {ingredienti.map(ing => (
                <option key={ing.id} value={ing.id}>
                  {ing.nome}
                </option>
              ))}
            </Select>
          </FormGroup>
        </LocalFilterRow>
        {analyticsIngredienti.length > 0 ? (
          <GelatoDataGrid
            rowData={analyticsIngredienti}
            columnDefs={ingredientiColumnDefs}
            pagination={false}
            height="600px"
            exportable={true}
            pinnedBottomRowData={[
              {
                nome: 'TOTALE',
                consumo_totale: null,
                costo_totale: analyticsIngredienti.reduce((sum, ing) => sum + ensureNumber(ing.costo_totale), 0),
                utilizzo_gusti: []
              }
            ]}
            excelFileName={`ingredienti-${annoLabel}-${dataOggi}.xlsx`}
            csvFileName={`ingredienti-${annoLabel}-${dataOggi}.csv`}
          />
        ) : (
          <EmptyNote>Nessun dato per i filtri selezionati.</EmptyNote>
        )}
      </SectionCard>
    </PageContainerWide>
  );
};

export default AnalyticsPage;
